import {
  ClassPackCard,
  ClassPackCardSkeleton,
} from '@/components/class-pack-card';
import { TopupListCard } from '@/components/topup-list-card';
import { FaqSection } from '@/components/faq-section';
import { IllustratedEmptyState } from '@/components/illustrated-empty-state';
import { getAllClassPacks } from '@/helpers/class-pack';
import { AppearanceSettings } from '@/state/appearance-settings';
import useClassPackGroups from '@/state/class-pack-groups';
import useTopup from '@/state/topup-content';
import useFaqs from '@/state/faqs';
import { useAuth } from '@/contexts/auth';
import { Separator } from '@mokoboko/components';

const EMPTY_COPY = {
  all: {
    title: 'No class packs found',
    description: (
      <>
        Once class packs are available,
        <br className="sm:hidden" /> they will appear here
      </>
    ),
  },
  forClass: {
    title: 'No packs for this class',
    description: 'No class pack includes this class right now.',
  },
};

export const ClassPacks = ({
  classPackSettings,
  // A section of the home page, but the whole of /packages — only the caller
  // knows which, and on /packages nothing else supplies the page's h1.
  headingAs: Heading = 'h2',
  showHeading = true,
  showFaq = true,
  classId,
}: {
  classPackSettings: AppearanceSettings['class_pack'];
  headingAs?: 'h1' | 'h2';
  showHeading?: boolean;
  showFaq?: boolean;
  classId?: string;
}) => {
  const { user } = useAuth();
  const { data: classPackGroups, isLoading: isClassPackGroupsLoading } =
    useClassPackGroups({ include_class_packs: true, class_id: classId });
  const { data: topup } = useTopup({ enabled: !!user });
  const { data: faqs } = useFaqs({
    entityType: 'CLASS_PACK',
    enabled: showFaq,
  });

  // Fixed copy, so the loading state draws it too.
  const heading = showHeading && (
    <div className="flex flex-col gap-1">
      <Heading className="heading-section">Class packs</Heading>
      <p className="text-sm text-gray-700">Flexible movement, on your terms</p>
    </div>
  );

  if (isClassPackGroupsLoading) {
    return (
      <div className="flex w-full flex-col gap-4 sm:gap-5">
        {heading}
        <ClassPackCardSkeleton />
        <ClassPackCardSkeleton />
        <ClassPackCardSkeleton />
      </div>
    );
  }

  const allClassPacks = getAllClassPacks(classPackGroups ?? []);

  if (!allClassPacks.length) {
    return (
      <IllustratedEmptyState
        illustration="class-pack-3d"
        {...EMPTY_COPY[classId ? 'forClass' : 'all']}
        className="py-6"
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 sm:gap-5">
      {heading}

      {user && topup && <TopupListCard topup={topup} />}

      {allClassPacks.map((classPack, index) => (
        <ClassPackCard
          key={index}
          classPack={classPack}
          classPackSettings={classPackSettings}
        />
      ))}

      {showFaq && faqs.length > 0 && (
        <>
          <Separator className="my-3" />
          <FaqSection faqs={faqs} className="max-sm:pt-0" />
        </>
      )}
    </div>
  );
};
